/**
 * MarkdownPreview — minimal markdown -> HTML for AI-drafted vault docs.
 *
 * Uses `marked` for parsing. Sanitization scope is limited because:
 *  - Source is a Sonnet 4 draft we just wrote (not user input).
 *  - The component renders inside a Dialog only the doc owner can open.
 * For shared-link previews, the public route should still call this since the
 * draft was authored by the same user/server.
 */
import { useMemo } from "react";
import { marked } from "marked";

const MARKED_OPTS = {
  gfm: true,
  breaks: false,
  headerIds: false,
  mangle: false,
};

export default function MarkdownPreview({ markdown, className = "" }) {
  const html = useMemo(() => {
    if (!markdown) return "";
    try {
      return marked.parse(markdown, MARKED_OPTS);
    } catch {
      return `<pre>${escapeHtml(markdown)}</pre>`;
    }
  }, [markdown]);

  return (
    <article
      data-testid="markdown-preview"
      className={
        "prose prose-zinc max-w-none text-[13.5px] leading-relaxed " +
        "prose-headings:font-extrabold prose-headings:tracking-tight " +
        "prose-h1:text-[24px] prose-h2:text-[18px] prose-h3:text-[15px] " +
        "prose-p:text-zinc-700 prose-li:text-zinc-700 prose-table:text-[12.5px] " +
        "prose-strong:text-zinc-900 prose-a:text-violet-700 " +
        className
      }
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
